import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { BuyerShell, BUYER_NAV } from "@/components/buyer/BuyerShell";
import { BuyerHome } from "@/components/buyer/BuyerHome";
import { BuyerOrders } from "@/components/buyer/BuyerOrders";
import { BuyerReturns } from "@/components/buyer/BuyerReturns";
import { BuyerWallet } from "@/components/buyer/BuyerWallet";
import { BuyerPayments } from "@/components/buyer/BuyerPayments";
import { BuyerMessages } from "@/components/buyer/BuyerMessages";
import { BuyerReviews } from "@/components/buyer/BuyerReviews";
import { BuyerCoupons } from "@/components/buyer/BuyerCoupons";
import { useChat } from "@/context/ChatContext";

const META = {
  dashboard: { title: "", subtitle: "" },
  orders: { title: "My Orders", subtitle: "Track and manage everything you've bought" },
  messages: { title: "Messages", subtitle: "Chat with the sellers you buy from" },
  returns: { title: "Returns & Refunds", subtitle: "Open and follow up on your requests" },
  reviews: { title: "Reviews", subtitle: "Rate the items you've received" },
  coupons: { title: "Coupons", subtitle: "Grab a code and save at checkout" },
  wallet: { title: "Wallet & Loyalty Points", subtitle: "Your points balance, store credit & history" },
  payments: { title: "Payment Methods", subtitle: "Manage your saved cards" },
  addresses: { title: "Addresses", subtitle: "Manage your delivery addresses" },
  profile: { title: "Profile", subtitle: "Your account details" },
};

export default function Account() {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { startConversation } = useChat();
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "dashboard";
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addr, setAddr] = useState({ label: "", full_name: "", phone: "", line1: "", city: "", country: "", postal_code: "" });
  const [returnPrefill, setReturnPrefill] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const setSection = (key) => {
    if (key === "wishlist") return navigate("/wishlist");
    setParams({ section: key });
  };

  const messageSeller = async (sellerId, productId) => {
    if (!sellerId) return;
    try {
      await startConversation(sellerId, productId, false);
      setParams({ section: "messages" });
    } catch {
      toast.error("Couldn't start the chat");
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    await api.post("/addresses", addr);
    setAddr({ label: "", full_name: "", phone: "", line1: "", city: "", country: "", postal_code: "" });
    toast.success("Address added");
    loadAll();
  };
  const delAddress = async (id) => { await api.delete(`/addresses/${id}`); loadAll(); };

  const requestReturn = (order) => { setReturnPrefill(order); setParams({ section: "returns" }); };

  const meta = META[section] || {};

  return (
    <BuyerShell active={section} onNavigate={setSection} title={meta.title} subtitle={meta.subtitle}>
      {section === "dashboard" && <BuyerHome userName={user?.name?.split(" ")[0]} />}

      {section === "orders" && <BuyerOrders orders={orders} onRequestReturn={requestReturn} onMessageSeller={messageSeller} />}

      {section === "messages" && <BuyerMessages />}

      {section === "returns" && (
        <BuyerReturns orders={orders} prefillOrder={returnPrefill} onConsumePrefill={() => setReturnPrefill(null)} />
      )}

      {section === "reviews" && <BuyerReviews />}

      {section === "coupons" && <BuyerCoupons />}

      {section === "wallet" && <BuyerWallet />}

      {section === "payments" && <BuyerPayments />}

      {section === "addresses" && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            {addresses.length === 0 && <p className="text-muted-foreground text-sm">No addresses saved yet.</p>}
            {addresses.map((a) => (
              <Card key={a.address_id} className="p-4 flex justify-between rounded-2xl">
                <div className="text-sm"><p className="font-semibold">{a.label} · {a.full_name}</p><p className="text-muted-foreground">{a.line1}, {a.city}, {a.postal_code}, {a.country}</p><p className="text-muted-foreground">{a.phone}</p></div>
                <button data-testid={`del-addr-${a.address_id}`} onClick={() => delAddress(a.address_id)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></button>
              </Card>
            ))}
          </div>
          <Card className="p-5 rounded-2xl">
            <h3 className="font-semibold mb-3">{t("addAddress")}</h3>
            <form onSubmit={addAddress} className="space-y-3">
              {[["label", t("labelField")], ["full_name", t("fullName")], ["phone", t("phoneLabel")], ["line1", t("addressLine")], ["city", t("cityField")], ["postal_code", t("postalField")], ["country", t("country")]].map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`addr-${k}`} required value={addr[k]} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} className="mt-1" /></div>
              ))}
              <Button data-testid="save-address-btn" type="submit" className="w-full rounded-full">{t("saveAddress")}</Button>
            </form>
          </Card>
        </div>
      )}

      {section === "profile" && (
        <Card className="p-6 max-w-md space-y-3 rounded-2xl">
          <div><Label className="text-xs text-muted-foreground">{t("fullName")}</Label><p className="font-medium">{user?.name}</p></div>
          <div><Label className="text-xs text-muted-foreground">{t("email")}</Label><p className="font-medium">{user?.email}</p></div>
          <div><Label className="text-xs text-muted-foreground">{t("roleField")}</Label><p className="font-medium capitalize">{user?.role}</p></div>
        </Card>
      )}
    </BuyerShell>
  );
}
